import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, Phone, Mail } from "lucide-react";
import { Eyebrow, FIRM } from "./_shared";
import WhatsAppIcon, { whatsappUrl } from "./WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
      });
      if (res.data?.success) {
        setSubmitted(true);
        setForm({ name: "", email: "", subject: "", message: "" });
        toast.success("Message received. We will be in touch shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't send your message right now. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-cream-50 border-t border-cream-300"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading + details */}
          <div className="lg:col-span-5 reveal">
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[1.05]">
              Begin a
              <span className="block italic text-sage-600">conversation.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-charcoal-muted font-sans leading-relaxed max-w-md">
              Tell us a little about your matter. We'll reply personally — usually
              within one business day.
            </p>

            <ul className="mt-12 space-y-6 max-w-md">
              {FIRM.offices.map((o) => (
                <li
                  key={o.city}
                  data-testid={`contact-office-${o.city.toLowerCase()}`}
                  className="flex gap-4"
                >
                  <MapPin className="text-sage-600 mt-1 shrink-0" size={18} />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
                      {o.city} · {o.country}
                    </p>
                    <div className="mt-1 font-serif text-lg text-charcoal leading-snug">
                      {o.addressLines.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
              <li className="flex gap-4">
                <Phone className="text-sage-600 mt-1 shrink-0" size={18} />
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
                    Telephone
                  </p>
                  {FIRM.offices.map((o) => (
                    <div key={o.city}>
                      <a
                        href={`tel:${o.phone.replace(/\s/g, "")}`}
                        data-testid={`contact-phone-${o.city.toLowerCase()}`}
                        className="font-serif text-lg text-charcoal hover:text-sage-700 transition-colors block"
                      >
                        {o.phone}
                      </a>
                      <a
                        href={whatsappUrl(o.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-whatsapp-${o.city.toLowerCase()}`}
                        className="mt-1 inline-flex items-center gap-2 font-sans text-[13px] text-charcoal-muted hover:text-sage-700 transition-colors"
                      >
                        <WhatsAppIcon className="h-4 w-4 text-sage-600" /> Chat on WhatsApp
                      </a>
                    </div>
                  ))}
                </div>
              </li>
              <li className="flex gap-4">
                <Mail className="text-sage-600 mt-1 shrink-0" size={18} />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
                    Email
                  </p>
                  <a
                    href={`mailto:${FIRM.email}`}
                    data-testid="contact-email-link"
                    className="mt-1 font-serif text-lg text-charcoal hover:text-sage-700 transition-colors block"
                  >
                    {FIRM.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 reveal">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-cream-100 border border-cream-300 p-10 lg:p-14"
              >
                <span className="text-[11px] uppercase tracking-[0.28em] text-sage-600 font-sans">
                  Received
                </span>
                <h3 className="mt-4 font-serif text-3xl lg:text-4xl text-charcoal leading-tight">
                  Thank you. Your message is with us.
                </h3>
                <p className="mt-4 text-charcoal-muted font-sans leading-relaxed max-w-lg">
                  A member of the firm will respond directly to the email you
                  provided. For urgent matters, please call {FIRM.phone}.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="mt-10 inline-flex items-center gap-3 border border-sage-600 text-charcoal hover:bg-sage-600 hover:text-cream-50 px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors"
                >
                  Send another note <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-cream-100 border border-cream-300 p-8 lg:p-12"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    required
                    testid="contact-input-name"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    required
                    testid="contact-input-email"
                  />
                </div>
                <div className="mt-8">
                  <Field
                    label="Subject (optional)"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    placeholder="Briefly, what is this about?"
                    testid="contact-input-subject"
                  />
                </div>
                <div className="mt-8">
                  <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans block">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    required
                    placeholder="Tell us a little about your matter…"
                    data-testid="contact-input-message"
                    className="mt-3 w-full bg-transparent border-b border-cream-300 focus:border-sage-600 focus:outline-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal-muted/60 resize-none transition-colors"
                  />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <p className="text-[12px] text-charcoal-muted font-sans max-w-sm leading-relaxed">
                    By submitting, you agree to be contacted regarding your
                    inquiry. No solicitor-client relationship is formed.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-3 bg-sage-600 text-cream-50 hover:bg-sage-700 disabled:opacity-60 disabled:cursor-not-allowed px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        Send message <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans block">
        {label}
        {required && <span className="text-sage-600"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-3 w-full bg-transparent border-b border-cream-300 focus:border-sage-600 focus:outline-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal-muted/60 transition-colors"
      />
    </div>
  );
}
